import React, { useState, useEffect } from "react";
import { Button, Textarea } from "@heroui/react";
import { HeartIcon, ChatBubbleLeftIcon } from "@heroicons/react/24/outline";
import { HeartIcon as HeartIconSolid, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/solid";
import { useAuth } from "../contexts/AuthContext";
import { createComment, getCommentReplies } from "../api/comments";
import { likeComment, unlikeComment } from "../api/likes";
import { useTheme } from "../themes";
import CommentErrorModal from "../components/CommentErrorModal";
import moment from "moment";

/**
 * AvatarWithFallback - Avatar component with error handling
 */
const AvatarWithFallback = ({ avatarUrl, username, size = "md" }) => {
  const [imgError, setImgError] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_BASE || "http://localhost:8080";

  const avatarSrc = avatarUrl && avatarUrl.trim() !== ''
    ? (avatarUrl.includes('googleusercontent.com')
        ? `${API_BASE_URL}/api/avatar-proxy?url=${encodeURIComponent(avatarUrl)}`
        : avatarUrl)
    : null;

  // Reset error state when avatarUrl changes
  useEffect(() => {
    setImgError(false);
  }, [avatarUrl]);

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || '?';
  };

  const sizeClasses = {
    sm: "w-6 h-6 sm:w-8 sm:h-8 text-[10px] ring-1 ring-[#2A9D8F]/10",
    md: "sm:w-10 sm:h-10 w-8 h-8 text-xs ring-2 ring-[#2A9D8F]/20"
  };

  return avatarSrc && !imgError ? (
    <img
      src={avatarSrc}
      alt={username}
      className={`flex-shrink-0 ${sizeClasses[size]} rounded-full object-cover`}
      onError={() => setImgError(true)}
    />
  ) : (
    <div className={`flex-shrink-0 ${sizeClasses[size]} rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold`}>
      {getInitials(username)}
    </div>
  );
};

/**
 * CommentThread - Displays a comment with its nested replies
 * Collapses replies after the 3rd one with an expand button
 */
const CommentThread = ({ comment, videoId, videoUploaderId, onLoginRequired, handleCommentLike, index, totalComments }) => {
  const { currentUser, getIdToken } = useAuth();
  const { currentMode } = useTheme();
  const isDark = currentMode === 'dark';

  const [replies, setReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [submittingReply, setSubmittingReply] = useState(false);
  const [expandedReplies, setExpandedReplies] = useState(false);

  const replyCount = comment.replyCount || 0;
  const COLLAPSE_THRESHOLD = 3;
  const [replyLikingInProgress, setReplyLikingInProgress] = useState(new Set());
  const [replyErrorModalOpen, setReplyErrorModalOpen] = useState(false);
  const [replyErrorType, setReplyErrorType] = useState('generic');

  // Auto-fetch replies on mount if comment has replies
  useEffect(() => {
    if (replyCount > 0 && replies.length === 0) {
      fetchReplies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReplies = async () => {
    setLoadingReplies(true);
    try {
      const result = await getCommentReplies(comment.id, getIdToken);
      if (result.success && result.data) {
        // Like states now included in backend response
        setReplies(result.data);
      }
    } catch (error) {
    } finally {
      setLoadingReplies(false);
    }
  };

  // Handle reply like/unlike
  const handleReplyLike = async (replyId) => {
    if (!currentUser) {
      onLoginRequired();
      return;
    }

    if (replyLikingInProgress.has(replyId)) {
      return;
    }

    setReplyLikingInProgress((prev) => new Set(prev).add(replyId));

    const reply = replies.find((r) => r.id === replyId);
    if (!reply) {
      return;
    }

    const previousLiked = reply.liked;
    const previousCount = reply.likeCount || 0;

    try {

      // Optimistic update
      setReplies((prevReplies) =>
        prevReplies.map((r) =>
          r.id === replyId
            ? {
                ...r,
                liked: !r.liked,
                likeCount: r.liked ? (r.likeCount || 0) - 1 : (r.likeCount || 0) + 1,
              }
            : r
        )
      );

      // Call API
      const result = reply.liked
        ? await unlikeComment(replyId, getIdToken)
        : await likeComment(replyId, getIdToken);

      if (result.success && result.data) {
        setReplies((prevReplies) =>
          prevReplies.map((r) =>
            r.id === replyId
              ? { ...r, liked: result.data.liked, likeCount: result.data.likeCount }
              : r
          )
        );
      } else {
        setReplies((prevReplies) =>
          prevReplies.map((r) =>
            r.id === replyId
              ? { ...r, liked: previousLiked, likeCount: previousCount }
              : r
          )
        );
      }
    } catch (error) {
      // ✅ FIX: Rollback to previous state, not toggle current state
      setReplies((prevReplies) =>
        prevReplies.map((r) =>
          r.id === replyId
            ? { ...r, liked: previousLiked, likeCount: previousCount }
            : r
        )
      );
    } finally {
      setReplyLikingInProgress((prev) => {
        const newSet = new Set(prev);
        newSet.delete(replyId);
        return newSet;
      });
    }
  };

  const handleReplySubmit = async () => {
    if (!replyText.trim()) return;

    if (!currentUser) {
      onLoginRequired();
      return;
    }

    setSubmittingReply(true);
    try {
      const result = await createComment(videoId, replyText, getIdToken, comment.id);
      if (result.success && result.data) {
        setReplies([...replies, result.data]);
        setReplyText("");
        setShowReplyInput(false);
        // Update parent comment reply count
        comment.replyCount = (comment.replyCount || 0) + 1;
      } else {
        // Check if error is EMAIL_NOT_VERIFIED
        if (result.error?.code === "EMAIL_NOT_VERIFIED" || result.error === "EMAIL_NOT_VERIFIED") {
          setReplyErrorType('EMAIL_NOT_VERIFIED');
          setReplyErrorModalOpen(true);
        } else {
          setReplyErrorType('generic');
          setReplyErrorModalOpen(true);
        }
      }
    } catch (error) {
      setReplyErrorType('generic');
      setReplyErrorModalOpen(true);
    } finally {
      setSubmittingReply(false);
    }
  };

  const toggleReplyInput = () => {
    if (!currentUser) {
      onLoginRequired();
      return;
    }
    setShowReplyInput(!showReplyInput);
  };

  // Determine which replies to show based on collapse state
  const displayedReplies = expandedReplies
    ? replies
    : replies.slice(0, COLLAPSE_THRESHOLD);
  const hiddenReplyCount = replies.length - COLLAPSE_THRESHOLD;

  return (
    <div className="relative">
      {/* Thread Line */}
      {index < totalComments - 1 && (
        <div className="absolute left-6 top-12 bottom-0 w-px bg-gradient-to-b from-[#2A9D8F]/30 via-[#2A9D8F]/10 to-transparent" />
      )}

      {/* Main Comment */}
      <div className="flex gap-2 sm:gap-3 relative z-10">
        <AvatarWithFallback avatarUrl={comment.userAvatar} username={comment.username} size="md" />
        <div className="flex-1 min-w-0 group">
          <div
            className="bg-white/5 backdrop-blur-xl rounded-xl sm:rounded-2xl px-3 py-3 sm:px-5 sm:py-4 border transition-all duration-300 hover:scale-[1.01] hover:bg-white/10 hover:border-[#2A9D8F]/30 hover:shadow-lg hover:shadow-[#2A9D8F]/5"
            style={
              isDark
                ? {
                    background: 'linear-gradient(125deg, rgb(0 0 0 / 15%) 0%, rgba(233, 196, 106, 0.12) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: '1.5px solid rgba(42, 157, 143, 0.2)'
                  }
                : {
                    borderColor: 'rgba(255, 255, 255, 0.1)'
                  }
            }
          >
            {/* User Info */}
            <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2 flex-wrap">
              <span className="font-semibold text-xs sm:text-sm text-default-900 dark:text-white truncate">
                {comment.username}
              </span>
              {videoUploaderId && comment.userId === videoUploaderId && (
                <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-primary text-primary-foreground">
                  OP
                </span>
              )}
              <span className="text-xs text-default-400">•</span>
              <span className="text-xs text-default-400">
                {moment(comment.createdAt).fromNow()}
              </span>
            </div>

            {/* Comment Content */}
            <p className="text-default-700 dark:text-gray-200 text-xs sm:text-[15px] leading-relaxed break-words">
              {comment.content}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 sm:gap-4 mt-2 px-2 sm:px-4">
            <Button
              size="sm"
              variant="light"
              startContent={
                comment.liked ? (
                  <HeartIconSolid className="w-4 h-4 sm:w-5 sm:h-5 text-[#E76F51]" />
                ) : (
                  <HeartIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-900 dark:text-white" />
                )
              }
              onPress={() => handleCommentLike(comment.id)}
              className={
                comment.liked
                  ? "text-[#E76F51] font-semibold hover:bg-[#E76F51]/10 min-w-0 px-2 sm:px-3 text-xs sm:text-sm backdrop-blur-md dark:!bg-transparent"
                  : "text-gray-900 dark:text-white font-semibold hover:text-[#E76F51] hover:bg-[#E76F51]/5 min-w-0 px-2 sm:px-3 text-xs sm:text-sm backdrop-blur-md dark:!bg-transparent"
              }
            >
              {comment.likeCount || 0}
            </Button>

            <Button
              size="sm"
              variant="light"
              startContent={<ChatBubbleLeftIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-900 dark:text-white" />}
              onPress={toggleReplyInput}
              className="text-gray-900 dark:text-white font-semibold hover:text-[#2A9D8F] hover:bg-[#2A9D8F]/5 min-w-0 px-2 sm:px-3 text-xs sm:text-sm backdrop-blur-md dark:!bg-transparent"
            >
              Reply
            </Button>
          </div>

          {/* Reply Input */}
          {showReplyInput && (
            <div className="mt-3 px-2 sm:px-4">
              <div className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-2 sm:p-3">
                <Textarea
                  placeholder="Write a reply..."
                  value={replyText}
                  onValueChange={setReplyText}
                  minRows={2}
                  maxRows={6}
                  variant="bordered"
                  maxLength={300}
                  classNames={{
                    input: "resize-y",
                  }}
                  description={`${replyText.length}/300 characters`}
                />
                <div className="flex gap-2 mt-2 justify-end">
                  <Button
                    size="sm"
                    variant="light"
                    onPress={() => {
                      setShowReplyInput(false);
                      setReplyText("");
                    }}
                    isDisabled={submittingReply}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    color="primary"
                    onPress={handleReplySubmit}
                    isLoading={submittingReply}
                    isDisabled={!replyText.trim() || replyText.length > 300}
                  >
                    Reply
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Replies Thread - Always visible if there are replies */}
          {(loadingReplies || replies.length > 0) && (
            <div className="mt-3 ml-4 sm:ml-8 space-y-2 sm:space-y-3 border-l-2 border-[#2A9D8F]/20 pl-2 sm:pl-4">
              {loadingReplies ? (
                <div className="text-center py-4 text-default-400 text-sm">
                  Loading replies...
                </div>
              ) : (
                <>
                  {displayedReplies.map((reply) => (
                    <div key={reply.id} className="flex gap-1.5 sm:gap-2">
                      <AvatarWithFallback avatarUrl={reply.userAvatar} username={reply.username} size="sm" />
                      <div className="flex-1 min-w-0">
                        <div className="bg-white/3 backdrop-blur-lg rounded-lg sm:rounded-xl px-2 py-1.5 sm:px-3 sm:py-2 border border-white/5">
                          <div className="flex items-center gap-1.5 sm:gap-2 mb-1 flex-wrap">
                            <span className="font-semibold text-[10px] sm:text-xs text-default-900 dark:text-white truncate">
                              {reply.username}
                            </span>
                            {videoUploaderId && reply.userId === videoUploaderId && (
                              <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-primary text-primary-foreground">
                                OP
                              </span>
                            )}
                            <span className="text-xs text-default-400">•</span>
                            <span className="text-xs text-default-400">
                              {moment(reply.createdAt).fromNow()}
                            </span>
                          </div>
                          <p className="text-default-700 dark:text-gray-200 text-[10px] sm:text-sm leading-relaxed break-words">
                            {reply.content}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-3 mt-1 px-1 sm:px-2">
                          <Button
                            size="sm"
                            variant="light"
                            startContent={
                              reply.liked ? (
                                <HeartIconSolid className="w-4 h-4 sm:w-5 sm:h-5 text-[#E76F51]" />
                              ) : (
                                <HeartIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-900 dark:text-white" />
                              )
                            }
                            onPress={() => handleReplyLike(reply.id)}
                            className={
                              reply.liked
                                ? "text-[#E76F51] font-semibold hover:bg-[#E76F51]/10 min-w-0 px-2 sm:px-3 text-xs sm:text-sm backdrop-blur-md dark:!bg-transparent"
                                : "text-gray-900 dark:text-white font-semibold hover:text-[#E76F51] hover:bg-[#E76F51]/5 min-w-0 px-2 sm:px-3 text-xs sm:text-sm backdrop-blur-md dark:!bg-transparent"
                            }
                          >
                            {reply.likeCount || 0}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Expand/Collapse Button */}
                  {replies.length > COLLAPSE_THRESHOLD && (
                    <Button
                      size="sm"
                      variant="flat"
                      onPress={() => setExpandedReplies(!expandedReplies)}
                      startContent={
                        expandedReplies ? (
                          <ChevronUpIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                        ) : (
                          <ChevronDownIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                        )
                      }
                      className="w-full bg-[#2A9D8F]/10 hover:bg-[#2A9D8F]/20 text-[#2A9D8F] text-xs sm:text-sm py-1.5 sm:py-2"
                    >
                      {expandedReplies
                        ? "Show less"
                        : `View ${hiddenReplyCount} more ${hiddenReplyCount === 1 ? "reply" : "replies"}`}
                    </Button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Reply Error Modal */}
      <CommentErrorModal
        isOpen={replyErrorModalOpen}
        onClose={() => setReplyErrorModalOpen(false)}
        errorType={replyErrorType}
      />
    </div>
  );
};

export default CommentThread;
